
import React, { useState, useMemo, useContext, useCallback, useEffect } from 'react';
import axios from 'axios';
import { API } from '../utils/api';
import { supabase } from '../lib/supabase';

const AuthContext = React.createContext(null);

export function useAuthContext() {
  return useContext(AuthContext);
}

// Dual-accept auth (Supabase migration):
//  - Social (Google/Apple) users authenticate via Supabase; their access token (a JWT) is
//    managed by the Supabase SDK and used as the Bearer. We mirror it into state so existing
//    consumers (headers) keep working unchanged.
//  - Email/password users keep PayMig's legacy opaque token in localStorage (unchanged).
// The effective token prefers the live Supabase session, else the legacy stored token.
export function useAuth() {
  const [legacyToken, setLegacyToken] = useState(localStorage.getItem('token'));
  const [sbToken, setSbToken] = useState(null);
  const [user, setUser] = useState(() => {
    const u = localStorage.getItem('user');
    return u ? JSON.parse(u) : null;
  });

  // Subscribe to Supabase auth state so the JWT (and refreshes) flow into our token.
  // If Supabase isn't configured (no anon key), skip — email/password login still works.
  useEffect(() => {
    if (!supabase) return;
    let mounted = true;
    supabase.auth.getSession().then(({ data }) => {
      if (mounted) setSbToken(data?.session?.access_token || null);
    });
    const { data: sub } = supabase.auth.onAuthStateChange((_event, session) => {
      setSbToken(session?.access_token || null);
    });
    return () => {
      mounted = false;
      sub?.subscription?.unsubscribe?.();
    };
  }, []);

  const token = sbToken || legacyToken;
  const headers = useMemo(() => ({ Authorization: token ? `Bearer ${token}` : '' }), [token]);

  // Legacy save/clear keep working for the email/password path.
  const save = (tok, usr) => {
    setLegacyToken(tok);
    setUser(usr);
    localStorage.setItem('token', tok);
    localStorage.setItem('user', JSON.stringify(usr));
  };

  // Drops both token sources so the effective `token` really goes null — AuthCallback
  // calls clear() directly without a Supabase signOut, so clearing only the legacy
  // token would leave a live sbToken in state.
  const clear = useCallback(() => {
    setLegacyToken(null);
    setSbToken(null);
    setUser(null);
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    // Signal any subscribers (e.g. NotificationBell) to drop cached data.
    try { window.dispatchEvent(new CustomEvent('paymig:auth-cleared')); } catch (_) { /* noop */ }
  }, []);

  const setProfile = useCallback((usr) => {
    setUser(usr);
    localStorage.setItem('user', JSON.stringify(usr));
  }, []);

  // Logout clears both the Supabase session and any legacy token.
  const logout = useCallback(async () => {
    try {
      if (legacyToken) {
        await axios.post(`${API}/auth/logout`, {}, { headers: { Authorization: `Bearer ${legacyToken}` } });
      }
    } catch (e) {
      console.log('Logout request failed, clearing local session');
    }
    try {
      if (supabase) await supabase.auth.signOut();
    } catch (e) {
      console.log('Supabase signOut failed, clearing local session');
    }
    clear();
  }, [legacyToken, clear]);

  return { token, user, headers, save, clear, setProfile, logout };
}

export const AuthProvider = AuthContext.Provider;
